import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Users, ChefHat, ArrowLeft, Edit, Trash2, MessageSquare, Utensils, Leaf, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Avatar } from '../components/Profile/Avatar';
import { Username } from '../components/Profile/Username';
import { CreateRecipeModal } from '../components/Recipes/CreateRecipeModal';
import { BookmarkButton } from '../components/BookmarkButton';
import { Meta } from '../components/Meta';
import { Schema } from '../components/Schema';
import { formatCategoryName } from '../lib/utils/formatters';
import type { Recipe } from '../types/recipes';

export function RecipeDetails() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOwnRecipe, setIsOwnRecipe] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState<Set<number>>(new Set());
  const [mobileTab, setMobileTab] = useState<'ingredients' | 'instructions'>('ingredients');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    async function fetchRecipe() {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        setIsAuthenticated(!!currentUser);
        
        // Check if the slug parameter looks like a UUID (old ID-based URL)
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug || '');
        
        let query;
        if (isUUID) {
          // If it's a UUID, try to find by ID and redirect to slug
          query = supabase
            .from('recipes')
            .select(`
              *,
              user:users (
                id,
                username,
                avatar_url,
                verified
              )
            `)
            .eq('id', slug)
            .single();
        } else {
          // If it's a slug, find by slug
          query = supabase
            .from('recipes')
            .select(`
              *,
              user:users (
                id,
                username,
                avatar_url,
                verified
              )
            `)
  .eq('slug', slug)
  .maybeSingle();
        }
        
        const { data, error } = await query;

        if (error) throw error;
        if (!data) throw new Error('Recipe not found');

        // If we found a recipe by ID (UUID), redirect to the slug URL
        if (isUUID && data.slug) {
          navigate(`/recipes/${data.slug}`, { replace: true });
          return;
        }

        setRecipe(data);
        setIsOwnRecipe(currentUser?.id === data.user_id);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchRecipe();
    }
  }, [slug, navigate, refreshKey]);

  const handleDelete = async () => {
    if (!recipe?.id || !window.confirm('Are you sure you want to delete this recipe? This action cannot be undone.')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('recipes')
        .delete()
        .eq('id', recipe.id);

      if (deleteError) throw deleteError;
      navigate('/recipes');
    } catch (err: any) {
      console.error('Error deleting recipe:', err);
      alert('Failed to delete recipe. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <Link 
          to="/recipes"
          className="inline-flex items-center text-accent-text hover:text-accent-text/80 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('detail.backTo', { section: t('nav.recipes') })}
        </Link>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-content mb-4">
            {error || t('common.noResults')}
          </h2>
          <Link 
            to="/recipes" 
            className="text-accent-text hover:text-accent-text/80"
          >
            {t('common.viewAll')}
          </Link>
        </div>
      </div>
    );
  }

  const totalTime = recipe.prep_time + recipe.cook_time;
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mindful.family';

  return (
    <>
      <Meta 
        title={`${recipe.title} - Healthy Recipe`}
        description={`Try this delicious ${recipe.title} recipe. ${recipe.description.substring(0, 150)}...`}
        image={recipe.image_url}
        type="article"
      />
      <Schema
        type="Recipe"
        data={{
          name: recipe.title,
          description: recipe.description,
          image: recipe.image_url || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&q=80&w=1920',
          url: `${baseUrl}/recipes/${recipe.slug}`,
          author: {
            name: recipe.user?.username || 'Anonymous',
            url: recipe.user?.id ? `${baseUrl}/profile/${recipe.user.id}/listings` : undefined,
          },
          datePublished: recipe.created_at,
          dateModified: recipe.updated_at,
          prepTime: recipe.prep_time.toString(),
          cookTime: recipe.cook_time.toString(),
          totalTime: totalTime.toString(),
          recipeYield: `${recipe.servings} servings`,
          recipeCategory: formatCategoryName(recipe.difficulty),
          recipeCuisine: formatCategoryName(recipe.cuisine_type),
          keywords: [
            formatCategoryName(recipe.cuisine_type),
            formatCategoryName(recipe.difficulty),
            ...recipe.dietary_preferences.map(pref => formatCategoryName(pref)),
          ],
          recipeIngredient: recipe.ingredients,
          recipeInstructions: recipe.instructions.map((instruction, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            text: instruction,
            name: `Step ${index + 1}`,
            url: `${baseUrl}/recipes/${recipe.slug}#step-${index + 1}`,
            image: recipe.image_url || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&q=80&w=1920',
          })),
          nutrition: {
            servingSize: `${recipe.servings} servings`,
          },
        }}
      />
      <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <Link 
        to="/recipes"
        className="inline-flex items-center text-accent-text hover:text-accent-text/80 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('detail.backTo', { section: t('nav.recipes') })}
      </Link>

      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 lg:h-96 rounded-2xl overflow-hidden mb-6">
        <img
          src={recipe.image_url || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?auto=format&fit=crop&q=80&w=1920'}
          alt={recipe.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Title */}
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-content mb-6">{recipe.title}</h1>

      {/* Organiser & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        {recipe.user && (
          <Link
            to={`/profile/${recipe.user.username || recipe.user.id}/listings`}
            className="flex items-center space-x-3 group"
          >
            <Avatar
              url={recipe.user.avatar_url}
              size="md"
              userId={recipe.user.id}
              editable={false}
            />
            <div>
              <Username
                username={recipe.user.username || 'Anonymous'}
                userId={recipe.user.id}
                verified={recipe.user.verified}
                className="block text-sm font-semibold text-content group-hover:text-accent-text transition-colors"
              />
              <span className="block text-xs text-content/45">
                {t('recipes.recipeCreator')}
              </span>
            </div>
          </Link>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {!isOwnRecipe && recipe.user && (
            <>
              {isAuthenticated ? (
                <Link
                  to={`/chat/${recipe.user.id}`}
                  className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-white bg-accent-text hover:bg-accent-text/90 rounded-lg transition-colors"
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  {t('profile.message')}
                </Link>
              ) : (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
                  className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-white bg-accent-text hover:bg-accent-text/90 rounded-lg transition-colors"
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  {t('profile.signInToMessage')}
                </button>
              )}
              <BookmarkButton
                targetId={recipe.id}
                targetType="recipes"
                className="bg-accent-text/5 hover:bg-accent-text/10"
              />
            </>
          )}
          {isOwnRecipe && (
                <>
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-accent-text bg-accent-text/5 hover:bg-accent-text/10 rounded-lg transition-colors"
                  >
                    <Edit className="h-3.5 w-3.5 mr-1.5" />
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    {t('common.delete')}
                  </button>
                </>
              )}
        </div>
      </div>

      {/* Recipe Details Card */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
        <div className="grid grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {/* Servings */}
          <div className="flex flex-col items-center text-center p-5">
            <div className="w-10 h-10 rounded-xl bg-accent-text/10 flex items-center justify-center mb-2">
              <Users className="w-[18px] h-[18px] text-accent-text" />
            </div>
            <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('recipes.serves')}</p>
            <p className="text-2xl font-bold text-content mt-1">{recipe.servings}</p>
          </div>
          {/* Time */}
          <div className="flex flex-col items-center text-center p-5">
            <div className="w-10 h-10 rounded-xl bg-accent-text/10 flex items-center justify-center mb-2">
              <Clock className="w-[18px] h-[18px] text-accent-text" />
            </div>
            <p className="text-[11px] font-semibold text-content/35 uppercase tracking-wider">{t('recipes.time')}</p>
            <p className="text-2xl font-bold text-content mt-1">{totalTime} min</p>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span className="inline-flex items-center px-3.5 py-1.5 text-sm font-medium bg-accent-text text-white rounded-full">
          {formatCategoryName(recipe.difficulty)}
        </span>
        <span className="inline-flex items-center px-3.5 py-1.5 text-sm font-medium bg-accent-text text-white rounded-full">
          {formatCategoryName(recipe.cuisine_type)}
        </span>
        {recipe.meal_type && (
          <span className="inline-flex items-center px-3.5 py-1.5 text-sm font-medium bg-accent-text text-white rounded-full">
            {formatCategoryName(recipe.meal_type)}
          </span>
        )}
      </div>

      {/* Description */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 mb-6">
        <h2 className="text-lg font-bold text-content mb-3">{t('detail.aboutRecipe')}</h2>
        <div className="text-[15px] text-content/70 whitespace-pre-line leading-relaxed">{recipe.description}</div>
      </div>

      {/* Mobile Toggle */}
      <div className="lg:hidden mb-6">
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setMobileTab('ingredients')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              mobileTab === 'ingredients'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500'
            }`}
          >
            {t('recipes.ingredients')}
          </button>
          <button
            onClick={() => setMobileTab('instructions')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              mobileTab === 'instructions'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500'
            }`}
          >
            {t('recipes.instructions')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingredients */}
        <div className={`bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 ${mobileTab === 'instructions' ? 'lg:block hidden' : ''}`}>
          <h2 className="text-lg font-bold text-content mb-4 flex items-center">
            <Utensils className="w-5 h-5 mr-2 text-accent-text" />
            {t('recipes.ingredients')}
          </h2>
          <ul className="space-y-3">
            {recipe.ingredients.map((ingredient, index) => {
              const isChecked = checkedIngredients.has(index);
              return (
                <li key={index} className="flex items-start text-content/80">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const newChecked = new Set(checkedIngredients);
                      if (e.target.checked) {
                        newChecked.add(index);
                      } else {
                        newChecked.delete(index);
                      }
                      setCheckedIngredients(newChecked);
                    }}
                    className="w-5 h-5 rounded border-gray-300 text-accent-text focus:ring-accent-text/20 cursor-pointer mr-3 flex-shrink-0 mt-0.5"
                  />
                  <span className={isChecked ? 'line-through text-content/50' : ''}>{ingredient}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Instructions */}
        <div className={`bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 ${mobileTab === 'ingredients' ? 'lg:block hidden' : ''}`}>
          <h2 className="text-lg font-bold text-content mb-4 flex items-center">
            <ChefHat className="w-5 h-5 mr-2 text-accent-text" />
            {t('recipes.instructions')}
          </h2>
          <ol className="space-y-4">
            {recipe.instructions.map((instruction, index) => (
              <li key={index} className="flex text-content/80">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-accent-text/10 text-accent-text flex items-center justify-center font-bold text-sm mr-3">{index + 1}</span>
                <span className="leading-relaxed">{instruction}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Dietary Preferences */}
      {recipe.dietary_preferences && recipe.dietary_preferences.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7 mt-6">
          <h2 className="text-lg font-bold text-content mb-3 flex items-center">
            <Leaf className="w-5 h-5 mr-2 text-accent-text" />
            {t('recipes.dietaryInfo')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {recipe.dietary_preferences.map((pref, index) => (
              <span
                key={index}
                className="px-3 py-1.5 text-sm font-medium bg-accent-text/5 text-accent-text rounded-full border border-accent-text/10"
              >
                {formatCategoryName(pref)}
              </span>
            ))}
          </div>
        </div>
      )}

      {showEditModal && (
        <CreateRecipeModal
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            setRefreshKey((k) => k + 1);
          }}
          recipe={recipe}
        />
      )}
      </div>
    </>
  );
}